/*
  Действия над счётом: сборка процессуального пакета и досудебного
  уведомления (20.10.2026).

  Смены стадии здесь нет: с 20.10.2026 (решение владельца, макет 1Б) она живёт в
  шапке карточки - `AccountStage.jsx`. Сборка пакета и смена стадии друг друга не
  запускают.

  Вынесено из `pages/AccountPage.jsx` 09.09.2026, когда страница перешагнула
  норму в 250 строк. Граница по скиллу `frontend`: страница грузит карточку и
  держит сообщения, действия знают только про свои поля и отдают исход наверх.

  Ошибка и успех не показываются здесь: они уходят на страницу и встают рядом с
  заголовком счёта - там же, где человек увидит изменившуюся стадию.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import ClaimChoiceDialog from './ClaimChoiceDialog.jsx'
import { Field, Notice } from './Common.jsx'
import CourtField from './CourtField.jsx'

/*
  «Должники: 2 из 3» - отмечено из всех, кого показывает окно. Числа приходят с
  карточкой счёта: грузить выбор целиком ради подписи значило бы лишний запрос
  на каждое открытие карточки.
*/
function choiceLabel(card) {
  if (!card.claim_total) return 'Должники'
  return `Должники: ${card.claim_claimed} из ${card.claim_total}`
}

/*
  Что написано под полем участка. Три разных положения дел, и человек обязан
  отличать их одно от другого: откуда взялось значение в поле, расходится ли оно
  со справочником и что делать, если участка нет вовсе.
*/
function courtHint(suggested, chosen) {
  if (!suggested) {
    return (
      'Дом этого счёта не привязан ни к одному участку, поэтому участок не подставлен. ' +
      'Выберите его из списка, впишите свой - или привяжите дом в «Настройках» → «Судебные участки», ' +
      'и он будет подставляться сам.'
    )
  }
  if (chosen.trim() === suggested) {
    return 'Участок подставлен по адресу помещения. Другой можно выбрать из списка или вписать своим текстом.'
  }
  return `По адресу помещения справочник даёт «${suggested}». Заявление уйдёт по тому, что стоит в поле.`
}

/*
  Сегодняшняя дата в том виде, которого ждёт поле даты и сервер: «2026-09-18».
  Берётся местная, а не UTC: в Москве после трёх часов ночи `toISOString()`
  отдаёт вчерашний день, и человек открыл бы форму с вчерашней датой.
*/
function today() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export default function Actions({ card, sections, sectionsError, onDone }) {
  /*
    Поле участка открывается ЗАПОЛНЕННЫМ - тем, что подставит справочник по
    адресу помещения (🔴 решение владельца 14.09.2026). Пустая строка читалась
    как «участок не определён», и человек вписывал его руками на каждое
    заявление - при заведённом справочнике.

    Карточка приходит с сервера один раз и меняется только вместе со счётом, а
    счёт - это своя страница: начальное значение состояния здесь верно и не
    требует синхронизации эффектом.
  */
  const [court, setCourt] = useState(card.court_section ?? '')
  /*
    Дата, на которую считается ВЕСЬ пакет (🔴 требование владельца 18.09.2026).
    От неё зависят давность, пени, пошлина и сведения в выписке из лицевого
    счёта - то есть все цифры документов разом, поэтому поле одно на пакет, а
    не по одному на документ.

    Сервер умел принимать её и раньше (`as_of`, по умолчанию сегодня), но
    кабинет её не передавал, и задним числом собрать пакет было нельзя - а это
    обычное дело: заявление готовят по состоянию на конец прошлого месяца.
  */
  const [asOf, setAsOf] = useState(today())
  const [busy, setBusy] = useState(false)
  /*
    🔴 Отказ действия живёт ЗДЕСЬ, а не поднимается странице (24.09.2026).
    Ошибка страницы означает «карточку не удалось загрузить», и страница
    показывает вместо неё пустой экран со ссылкой на реестр. Пока отказы
    сборки были редкими (нет реквизитов, не заведён дом), это сходило за
    мелочь; с гейтом банкротства отказ стал штатным ответом - счёт банкрота
    в суд не идёт, - и человек, нажав кнопку, терял карточку целиком.
    Правило 3 скилла `frontend`: ошибка показывается рядом с действием.
  */
  const [error, setError] = useState(null)
  /*
    Кому и как предъявляется требование (решение владельца 26.10.2026, макет Б2):
    кнопка рядом со сборкой пакета, на ней - сколько людей из скольких отмечено.
    Выбор хранится на счёте, поэтому окно читает его с сервера по нажатию, а не
    держит здесь: состав могли поменять на вкладке «Должники».
  */
  const [choice, setChoice] = useState(null)

  async function openChoice() {
    setError(null)
    try {
      setChoice(await api.claimChoice(card.account_id, asOf))
    } catch (failure) {
      setError(failure)
    }
  }

  function choiceSaved() {
    setChoice(null)
    onDone('Выбор должников сохранён: по нему соберутся пакет и уведомление.', 'ok')
  }

  async function buildPackage() {
    setBusy(true)
    setError(null)
    try {
      /*
        Уходит ровно то, что человек видит в поле, - включая подставленный
        участок. Отправлять вместо него пустоту, «потому что значение и так из
        справочника», значит показывать одно, а подавать по другому: справочник
        мог измениться после того, как карточка открылась.
      */
      const documents = await api.buildPackage(card.account_id, court, asOf)
      onDone(`Сформировано документов: ${documents.length}. Они ниже, в списке документов.`, 'ok')
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  /*
    Досудебное уведомление (решение владельца 20.10.2026) - на ту же «Дату
    пакета», что и заявление: уведомление прикладывается к нему, и суммы обязаны
    совпасть. Участок уведомлению не нужен, оно идёт должнику.
  */
  async function buildNotice() {
    setBusy(true)
    setError(null)
    try {
      const documents = await api.buildNotice(card.account_id, asOf)
      onDone(
        `Сформировано уведомлений: ${documents.length}. Они ниже, в списке документов; ` +
          'ссылка на оплату в каждом действует 45 дней.',
        'ok',
      )
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="section card">
      <h2 className="section__title">Судебный пакет и уведомление</h2>
      <div className="row">
        <CourtField
          label="Судебный участок"
          value={court}
          onChange={setCourt}
          sections={sections}
          error={sectionsError}
          placeholder="выберите из списка или впишите свой"
        />
        <Field label="Дата пакета" width="s">
          <input
            className="field__input"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </Field>
        <button
          className="button button--quiet"
          type="button"
          onClick={openChoice}
          disabled={busy}
        >
          {choiceLabel(card)}
        </button>
        <button className="button" type="button" onClick={buildPackage} disabled={busy || !asOf}>
          Сформировать пакет
        </button>
        <button
          className="button button--quiet"
          type="button"
          onClick={buildNotice}
          disabled={busy || !asOf}
        >
          Сформировать уведомление
        </button>
      </div>
      {/* Пояснение стоит ПОД строкой с кнопкой, а не под своим полем: внутри
          строки оно удлинило бы поле снизу и увело кнопку вниз (правило 6г).
          14.09.2026 текст переписан вслед за полем: оно теперь открывается
          заполненным, и объяснять надо не «оставьте пустым», а откуда взялось
          то, что человек видит. */}
      <Notice error={error} />
      <div className="muted">{courtHint(card.court_section, court)}</div>
      {/* Про дату сказано отдельной строкой: она влияет не на один документ, а
          на все цифры пакета сразу, и человек должен понимать, что меняет. */}
      <div className="muted">
        Дата пакета - та, на которую считаются долг, пени, пошлина и срок давности. По умолчанию
        сегодняшняя; поставьте конец месяца, если пакет готовится по состоянию на него.
      </div>
      {choice ? (
        <ClaimChoiceDialog
          accountId={card.account_id}
          accountNumber={card.number}
          choice={choice}
          asOf={asOf}
          onClose={() => setChoice(null)}
          onSaved={choiceSaved}
        />
      ) : null}
    </section>
  )
}
